import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { type User as FirebaseUser } from 'firebase/auth';
import {
  Card,
  useAuth,
  SocialLoginButtons,
  useToaster,
} from '../../../shared/ui';
import {
  signInWithEmailPassword,
  resendVerificationEmail,
  EmailNotVerifiedError,
} from '../../../shared/utils/firebase';
import './LoginPage.css';

export const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState<FirebaseUser | null>(
    null
  );
  const [resendLoading, setResendLoading] = useState(false);
  const { socialLogin, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const toaster = useToaster();

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Check if there's a redirect URL stored
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        // Check if it's an internal route
        if (redirectUrl.startsWith('/')) {
          navigate({ to: redirectUrl });
        } else {
          window.location.href = redirectUrl; // External URL
        }
      } else {
        // Default redirect to dashboard
        navigate({ to: '/' });
      }
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setUnverifiedUser(null);

    try {
      const idToken = await signInWithEmailPassword(email, password);
      const success = await socialLogin(idToken);

      if (success) {
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          if (redirectUrl.startsWith('/')) {
            navigate({ to: redirectUrl });
          } else {
            window.location.href = redirectUrl;
          }
        } else {
          navigate({ to: '/' });
        }
      }
    } catch (err) {
      if (err instanceof EmailNotVerifiedError) {
        setUnverifiedUser(err.firebaseUser);
      } else {
        const code = (err as { code?: string })?.code ?? '';
        if (
          code === 'auth/wrong-password' ||
          code === 'auth/user-not-found' ||
          code === 'auth/invalid-credential' ||
          code === 'auth/invalid-email'
        ) {
          toaster.error(
            'Invalid email or password. Please check your credentials and try again.'
          );
        } else if (code === 'auth/too-many-requests') {
          toaster.error('Too many failed attempts. Please try again later.');
        } else {
          toaster.error('Sign in failed. Please try again.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser || resendLoading) return;
    setResendLoading(true);
    try {
      await resendVerificationEmail(unverifiedUser);
    } finally {
      setResendLoading(false);
    }
  };

  const handleRegisterClick = () => {
    navigate({ to: '/register' });
  };

  return (
    <div className="login-page">
      <div className="login-page__container">
        <Card>
          <Box className="login-page__card-inner" component="div">
            <Typography variant="h4" className="login-page__title" gutterBottom>
              {t('login.title')}
            </Typography>
            <Typography
              variant="body1"
              className="login-page__subtitle"
              color="text.secondary"
            >
              {t('login.welcomeMessage')}
            </Typography>

            {/* Social login buttons live outside the email/password form to avoid nested <form> */}
            <SocialLoginButtons
              onSuccess={() => navigate({ to: '/' })}
              onMagicLinkSent={sentEmail =>
                navigate({
                  to: '/check-inbox',
                  state: { email: sentEmail, reason: 'magic-link' },
                })
              }
            />

            <Divider className="login-page__social-divider">
              <Typography variant="body2" color="text.secondary">
                or sign in with email & password
              </Typography>
            </Divider>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {unverifiedUser && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                    >
                      {resendLoading ? 'Sending...' : 'Resend'}
                    </Button>
                  }
                >
                  Please verify your email before signing in. Check your inbox.
                </Alert>
              )}
              <TextField
                label={t('common.email')}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                fullWidth
                margin="normal"
                variant="outlined"
                autoComplete="email"
              />

              <TextField
                label={t('common.password')}
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                fullWidth
                margin="normal"
                variant="outlined"
                autoComplete="current-password"
              />

              <Box className="login-page__submit">
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('common.signingIn') || 'Signing in...'
                    : t('common.signIn')}
                </Button>
              </Box>

              <Box className="login-page__footer">
                <Typography variant="body2" className="login-page__footer-text">
                  {t('common.dontHaveAccount')}{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleRegisterClick}
                    className="login-page__register-btn"
                  >
                    {t('common.signUp')}
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </div>
    </div>
  );
};
